import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Filter,
  Megaphone,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
} from "lucide-react";
import { api, asItems, asObject, formatApiError, formatPrice } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const VIEWS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "agents", label: "Agents", icon: UserCheck },
  { id: "properties", label: "Properties", icon: Building2 },
  { id: "leads", label: "Leads", icon: Megaphone },
  { id: "site-visits", label: "Site Visits", icon: CalendarCheck },
];

const AGENT_STATUSES = ["", "ACTIVE", "PENDING", "BLOCKED"];
const PROPERTY_STATUSES = ["", "DRAFT", "NEW", "SUBMITTED", "PENDING", "PENDING_VERIFICATION", "PENDING_FINAL_APPROVAL", "IN_PROCESS", "VERIFIED", "PUBLISHED", "PENDING_SOLD_VERIFICATION", "BOOKED", "SOLD", "REJECTED", "ARCHIVED"];
const PROPERTY_CATEGORIES = ["", "LAND", "PLOT", "HOUSE", "VILLA", "APARTMENT", "FLAT", "COMMERCIAL"];
// Keep this list aligned with LeadController.ALLOWED_LEAD_STATUSES so the
// Operations filter never offers an impossible state or hides a real one.
const LEAD_STATUSES = ["", "NEW", "ASSIGNED", "CONTACTED", "INTERESTED", "SITE_VISIT_SCHEDULED", "NEGOTIATION", "BOOKED", "SOLD", "COMPLETED", "APPROVED", "REJECTED", "LOST", "DISCARDED"];
const LEAD_SOURCES = ["", "CONTACT", "PLOT_DEMAND", "PHONE_REVEAL", "WHATSAPP_CLICK"];
const VISIT_STATUSES = ["", "PENDING", "REQUESTED", "OPEN", "ASSIGNED", "SCHEDULED", "CONFIRMED", "IN_PROGRESS", "COMPLETED", "CANCELLED", "NO_SHOW"];
const VISIT_DATES = ["", "today", "upcoming", "overdue"];

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function AdminOperations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const initialView = VIEWS.some((view) => view.id === requestedView) ? requestedView : "overview";
  const [activeView, setActiveView] = useState(initialView);
  const [summary, setSummary] = useState(null);
  const [overviewAgents, setOverviewAgents] = useState([]);
  const [overviewProperties, setOverviewProperties] = useState([]);
  const [overviewVisits, setOverviewVisits] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");
  const [tableQuery, setTableQuery] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [secondaryFilter, setSecondaryFilter] = useState("");
  const [scopeAgentId, setScopeAgentId] = useState(searchParams.get("agent_id") || "");
  const [scopePropertyId, setScopePropertyId] = useState(searchParams.get("property_id") || "");
  const [globalQuery, setGlobalQuery] = useState("");
  const [globalResults, setGlobalResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const lastUrlView = useRef(initialView);
  const debouncedTableQuery = useDebouncedValue(tableQuery);
  const debouncedGlobalQuery = useDebouncedValue(globalQuery, 250);
  const pageSize = 20;

  const loadOverview = async () => {
    setLoadingOverview(true);
    setError("");
    try {
      const [summaryResponse, agentResponse, propertyResponse, visitResponse] = await Promise.allSettled([
        api.get("/v1/admin/operations/summary"),
        api.get("/v1/admin/operations/agents", { params: { limit: 6, sort: "workload" } }),
        api.get("/v1/admin/operations/properties", { params: { limit: 6, sort: "activity" } }),
        api.get("/v1/admin/operations/site-visits", { params: { limit: 6 } }),
      ]);
      setSummary(summaryResponse.status === "fulfilled" ? asObject(summaryResponse.value.data) : {});
      setOverviewAgents(agentResponse.status === "fulfilled" ? asItems(agentResponse.value.data) : []);
      setOverviewProperties(propertyResponse.status === "fulfilled" ? asItems(propertyResponse.value.data) : []);
      setOverviewVisits(visitResponse.status === "fulfilled" ? asItems(visitResponse.value.data) : []);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoadingOverview(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    const viewFromUrl = searchParams.get("view");
    if (VIEWS.some((view) => view.id === viewFromUrl)) {
      setActiveView((current) => current === viewFromUrl ? current : viewFromUrl);
      if (lastUrlView.current !== viewFromUrl) {
        setStatusFilter("");
        setSecondaryFilter("");
        lastUrlView.current = viewFromUrl;
      }
    }
    const queryFromUrl = searchParams.get("q") || "";
    setTableQuery((current) => current === queryFromUrl ? current : queryFromUrl);
    setPage(1);
    setScopeAgentId(searchParams.get("agent_id") || "");
    setScopePropertyId(searchParams.get("property_id") || "");
  }, [searchParams]);

  useEffect(() => {
    if (activeView === "overview") return undefined;
    let current = true;
    const loadRows = async () => {
      setLoadingRows(true);
      setError("");
      try {
        const params = {
          q: debouncedTableQuery || undefined,
          status: statusFilter || undefined,
          page,
          limit: pageSize,
        };
        let endpoint = "/v1/admin/operations/agents";
        if (activeView === "properties") {
          endpoint = "/v1/admin/operations/properties";
          params.category = secondaryFilter || undefined;
          params.agent_id = scopeAgentId || undefined;
        } else if (activeView === "leads") {
          endpoint = "/v1/admin/operations/leads";
          params.source = secondaryFilter || undefined;
          params.agent_id = scopeAgentId || undefined;
          params.property_id = scopePropertyId || undefined;
        } else if (activeView === "site-visits") {
          endpoint = "/v1/admin/operations/site-visits";
          params.date = secondaryFilter || undefined;
          params.agent_id = scopeAgentId || undefined;
          params.property_id = scopePropertyId || undefined;
        }
        const response = await api.get(endpoint, { params });
        if (!current) return;
        const data = asObject(response.data);
        setRows(asItems(data));
        setTotal(Number(data.total || 0));
      } catch (requestError) {
        if (current) {
          setRows([]);
          setTotal(0);
          setError(formatApiError(requestError));
        }
      } finally {
        if (current) setLoadingRows(false);
      }
    };
    loadRows();
    return () => { current = false; };
  }, [activeView, debouncedTableQuery, statusFilter, secondaryFilter, scopeAgentId, scopePropertyId, page, reloadKey]);

  useEffect(() => {
    let current = true;
    if (debouncedGlobalQuery.trim().length < 2) {
      setGlobalResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    api.get("/v1/admin/operations/search", { params: { q: debouncedGlobalQuery.trim(), limit: 20 } })
      .then((response) => {
        if (current) setGlobalResults(asItems(response.data));
      })
      .catch((requestError) => {
        if (current) {
          setGlobalResults([]);
          toast.error(`Admin search could not be completed: ${formatApiError(requestError) || "Request failed."}`);
        }
      })
      .finally(() => {
        if (current) setSearching(false);
      });
    return () => { current = false; };
  }, [debouncedGlobalQuery]);

  const metrics = summary?.metrics || {};
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const hasGlobalSearch = globalQuery.trim().length >= 2;

  const changeView = (view) => {
    setActiveView(view);
    setPage(1);
    setTableQuery("");
    setStatusFilter("");
    setSecondaryFilter("");
    setScopeAgentId("");
    setScopePropertyId("");
    const next = new URLSearchParams();
    next.set("view", view);
    setSearchParams(next, { replace: true });
  };

  const refresh = () => {
    if (activeView === "overview") {
      loadOverview();
      return;
    }
    setReloadKey((value) => value + 1);
  };

  const clearScope = () => {
    setScopeAgentId("");
    setScopePropertyId("");
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete("agent_id");
    next.delete("property_id");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="admin-operations-page">
      <section className="relative overflow-visible rounded-3xl border border-border bg-card p-5 shadow-sm sm:p-7">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-3xl bg-gradient-to-r from-primary via-warning to-destructive" />
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-2xl">
            <div className="mb-2 flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.22em] text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-warning" />
              Admin control centre
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl">
              Operations <span className="brand-gradient-text">Hub</span>
            </h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground sm:text-base">
              Find agent workloads, property activity, buyers, leads, offices, Site Visits, and Plots on Demand from one place.
            </p>
          </div>

          <div className="relative w-full xl:max-w-xl">
            <label htmlFor="admin-global-search" className="mb-2 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Search across operations
            </label>
            <Search className="pointer-events-none absolute bottom-3.5 left-4 h-4 w-4 text-muted-foreground" />
            <Input
              id="admin-global-search"
              value={globalQuery}
              onChange={(event) => setGlobalQuery(event.target.value)}
              maxLength={200}
              placeholder="Agent, buyer, property, office, lead, Site Visit or Plots on Demand"
              className="h-12 rounded-2xl bg-background pl-11 pr-12"
              autoComplete="off"
              title="Search agents, buyers, properties, offices, leads, Site Visits and Plots on Demand"
            />
            {searching && <RefreshCw className="absolute bottom-3.5 right-4 h-4 w-4 animate-spin text-primary" />}
            {hasGlobalSearch && (
              <GlobalSearchResults
                items={globalResults}
                loading={searching}
                onClose={() => setGlobalQuery("")}
              />
            )}
          </div>
        </div>
      </section>

      <nav className="flex gap-2 overflow-x-auto rounded-2xl border border-border bg-card p-2 shadow-sm" aria-label="Operations categories">
        {VIEWS.map((view) => {
          const Icon = view.icon;
          const active = activeView === view.id;
          return (
            <button
              key={view.id}
              type="button"
              onClick={() => changeView(view.id)}
              className={`flex min-w-max items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all duration-200 ease-out ${active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
              aria-current={active ? "page" : undefined}
              title={`Open ${view.label}`}
            >
              <Icon className="h-4 w-4" />
              {view.label}
            </button>
          );
        })}
      </nav>

      {error && (
        <div className="flex flex-col gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between" role="alert">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={refresh} title="Try loading operations data again">
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </div>
      )}

      {activeView === "overview" ? (
        <OverviewPanel
          loading={loadingOverview}
          metrics={metrics}
          summary={summary}
          agents={overviewAgents}
          properties={overviewProperties}
          visits={overviewVisits}
          onOpenView={changeView}
        />
      ) : (
        <CategoryPanel
          view={activeView}
          rows={rows}
          total={total}
          loading={loadingRows}
          query={tableQuery}
          onQueryChange={(value) => { setTableQuery(value); setPage(1); }}
          status={statusFilter}
          onStatusChange={(value) => { setStatusFilter(value); setPage(1); }}
          secondary={secondaryFilter}
          onSecondaryChange={(value) => { setSecondaryFilter(value); setPage(1); }}
          scopeAgentId={scopeAgentId}
          scopePropertyId={scopePropertyId}
          onClearScope={clearScope}
          page={page}
          maxPage={maxPage}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function OverviewPanel({ loading, metrics, summary, agents, properties, visits, onOpenView }) {
  const cards = [
    { label: "Active agents", value: metrics.active_agents, icon: UserCheck, view: "agents", hint: "Available team" },
    { label: "Unassigned leads", value: metrics.unassigned_leads, icon: AlertTriangle, path: "/dashboard/admin/lead-assignments", hint: "Needs an owner", urgent: Number(metrics.unassigned_leads) > 0 },
    { label: "Visits today", value: metrics.visits_today, icon: CalendarCheck, view: "site-visits", hint: "Today's schedule" },
    { label: "Awaiting assignment", value: metrics.visits_awaiting_assignment, icon: Clock3, view: "site-visits", hint: "Visits without agent", urgent: Number(metrics.visits_awaiting_assignment) > 0 },
    { label: "Overdue visits", value: metrics.overdue_visits, icon: AlertTriangle, view: "site-visits", hint: "Needs follow-up", urgent: Number(metrics.overdue_visits) > 0 },
    { label: "In progress", value: metrics.visits_in_progress, icon: Activity, view: "site-visits", hint: "Live site visits" },
    { label: "Pending properties", value: metrics.properties_pending_review, icon: Building2, view: "properties", hint: "Review queue" },
    { label: "Total leads", value: metrics.leads, icon: Users, view: "leads", hint: `${formatCount(metrics.leads_today)} added today` },
  ];

  if (loading) return <OverviewSkeleton />;

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Operations summary">
        {cards.map(({ label, value, icon: Icon, view, path, hint, urgent }) => {
          const className = `group rounded-2xl border bg-card p-4 text-left shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md ${urgent ? "border-destructive/30" : "border-border"}`;
          const content = <>
            <div className="flex items-start justify-between gap-3">
              <span className={`rounded-xl p-2 ${urgent ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                <Icon className="h-4 w-4" />
              </span>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
            </div>
            <p className="mt-4 text-2xl font-black tabular-nums text-foreground sm:text-3xl">{formatCount(value)}</p>
            <p className="mt-1 text-sm font-bold text-foreground">{label}</p>
            <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
          </>;
          return path ? (
            <Link key={label} to={path} className={className} title={`Open ${label.toLowerCase()}`}>{content}</Link>
          ) : (
            <button key={label} type="button" onClick={() => onOpenView(view)} className={className} title={`Open ${view.replace("-", " ")}`}>{content}</button>
          );
        })}
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <AttentionCard
          title="Unassigned leads"
          subtitle="Oldest open leads waiting for an agent"
          empty="All open leads have an assigned agent."
          items={summary?.unassigned_leads || []}
          type="lead"
        />
        <AttentionCard
          title="Overdue site visits"
          subtitle="Scheduled time passed without a closing status"
          empty="No overdue site visits."
          items={summary?.overdue_site_visits || []}
          type="visit"
        />
      </section>

      <PreviewSection title="Agent workload" subtitle="Open leads plus upcoming site visits" action="View all agents" onAction={() => onOpenView("agents")}>
        <AgentTable rows={agents} compact />
      </PreviewSection>

      <PreviewSection title="Property activity" subtitle="Listings with the latest lead or visit activity" action="View all properties" onAction={() => onOpenView("properties")}>
        <PropertyTable rows={properties} compact />
      </PreviewSection>

      <PreviewSection title="Site visit schedule" subtitle="Upcoming and overdue visits across the platform" action="View all site visits" onAction={() => onOpenView("site-visits")}>
        <VisitTable rows={visits} compact />
      </PreviewSection>
    </div>
  );
}

function CategoryPanel({ view, rows, total, loading, query, onQueryChange, status, onStatusChange, secondary, onSecondaryChange, scopeAgentId, scopePropertyId, onClearScope, page, maxPage, onPageChange }) {
  const config = useMemo(() => {
    if (view === "properties") return {
      title: "Property activity",
      subtitle: "See who listed each property and how many buyers, leads, and visits it generated.",
      statuses: PROPERTY_STATUSES,
      secondaryOptions: PROPERTY_CATEGORIES,
      secondaryLabel: "All categories",
      placeholder: "Search title, code, city or locality",
    };
    if (view === "site-visits") return {
      title: "Site visit workflow",
      subtitle: "Find every buyer visit, assigned agent, office, property, schedule, and current state.",
      statuses: VISIT_STATUSES,
      secondaryOptions: VISIT_DATES,
      secondaryLabel: "Any date",
      placeholder: "Search property, buyer or agent",
    };
    if (view === "leads") return {
      title: "Lead relationships",
      subtitle: "See each buyer lead with its property, assigned agent, office, site visits, and current state.",
      statuses: LEAD_STATUSES,
      secondaryOptions: LEAD_SOURCES,
      secondaryLabel: "All sources",
      placeholder: "Search lead, buyer, property, agent or office",
    };
    return {
      title: "Agent workload",
      subtitle: "Compare each agent's assigned properties, active leads, and site visit workload.",
      statuses: AGENT_STATUSES,
      secondaryOptions: [],
      secondaryLabel: "",
      placeholder: "Search agent name, email or phone",
    };
  }, [view]);

  return (
    <section className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
      <header className="border-b border-border p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Operations directory</p>
            <h2 className="mt-1 font-display text-2xl font-black text-foreground sm:text-3xl">{config.title}</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{config.subtitle}</p>
          </div>
          <Badge variant="outline" className="w-fit rounded-full px-3 py-1.5 tabular-nums">
            {formatCount(total)} results
          </Badge>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem] xl:grid-cols-[minmax(0,1fr)_12rem_12rem]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              maxLength={200}
              placeholder={config.placeholder}
              className="h-11 rounded-xl pl-10"
              title={config.placeholder}
            />
          </div>
          <FilterSelect value={status} onChange={onStatusChange} options={config.statuses} emptyLabel="All statuses" />
          {config.secondaryOptions.length > 0 && (
            <FilterSelect value={secondary} onChange={onSecondaryChange} options={config.secondaryOptions} emptyLabel={config.secondaryLabel} />
          )}
        </div>
        {(scopeAgentId || scopePropertyId) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-xs">
            <span className="font-bold text-primary">Scoped drill-down:</span>
            {scopeAgentId && <span className="text-foreground">agent {shortId(scopeAgentId)}</span>}
            {scopePropertyId && <span className="text-foreground">property {shortId(scopePropertyId)}</span>}
            <button type="button" onClick={onClearScope} className="ml-auto font-bold text-primary hover:underline" title="Clear agent and property drill-down filters">Clear scope</button>
          </div>
        )}
      </header>

      <div className="p-3 sm:p-5">
        {loading ? (
          <RowsSkeleton />
        ) : rows.length === 0 ? (
          <EmptyResults />
        ) : view === "properties" ? (
          <PropertyTable rows={rows} />
        ) : view === "leads" ? (
          <LeadTable rows={rows} />
        ) : view === "site-visits" ? (
          <VisitTable rows={rows} />
        ) : (
          <AgentTable rows={rows} />
        )}
      </div>

      <footer className="flex flex-col gap-3 border-t border-border px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between">
        <span className="text-muted-foreground">Page {page} of {maxPage}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => onPageChange(page - 1)} title="Previous results page">
            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
          </Button>
          <Button variant="outline" size="sm" disabled={page >= maxPage || loading} onClick={() => onPageChange(page + 1)} title="Next results page">
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </footer>
    </section>
  );
}

function AgentTable({ rows, compact = false }) {
  if (!rows.length) return <EmptyResults compact />;
  const shownRows = compact ? rows.slice(0, 6) : rows;
  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[880px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-3">Agent</th><th className="px-3 py-3">Office</th><th className="px-3 py-3 text-right">Properties</th>
              <th className="px-3 py-3 text-right">Open / total leads</th><th className="px-3 py-3 text-right">Upcoming / total visits</th>
              <th className="px-3 py-3">Last activity</th><th className="px-3 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {shownRows.map((agent) => (
              <tr key={agent.user_id} className="border-b border-border/70 last:border-0 hover:bg-secondary/40">
                <td className="px-3 py-4">
                  <EntityLink type="user" id={agent.user_id} title="Open agent profile" className="font-bold text-foreground">{agent.name || "Unnamed agent"}</EntityLink>
                  <p className="mt-0.5 text-xs text-muted-foreground">Workload score {formatCount(agent.workload_score)}</p>
                </td>
                <td className="px-3 py-4">{agent.office_id ? <EntityLink type="office" id={agent.office_id} title="Open office details">{agent.office_name || "Assigned office"}</EntityLink> : <span className="text-muted-foreground">Independent</span>}</td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums"><Link to={operationScopeUrl("properties", "agent_id", agent.user_id)} title="View properties attributed to this agent" className="text-primary hover:underline">{formatCount(agent.property_count)}</Link></td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums"><Link to={operationScopeUrl("leads", "agent_id", agent.user_id)} title="View leads assigned to this agent" className="text-primary hover:underline">{formatCount(agent.open_lead_count)} / {formatCount(agent.total_lead_count)}</Link></td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums"><Link to={operationScopeUrl("site-visits", "agent_id", agent.user_id)} title="View site visits assigned to this agent" className="text-primary hover:underline">{formatCount(agent.upcoming_site_visit_count)} / {formatCount(agent.total_site_visit_count)}</Link></td>
                <td className="px-3 py-4 text-muted-foreground">{formatDateTime(agent.last_activity_at, true)}</td>
                <td className="px-3 py-4"><StatusPill status={agent.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-3 lg:hidden">
        {shownRows.map((agent) => (
          <article key={agent.user_id} className="rounded-2xl border border-border bg-background p-4">
            <div className="flex items-start justify-between gap-3">
              <div><EntityLink type="user" id={agent.user_id} title="Open agent profile" className="font-bold text-foreground">{agent.name || "Unnamed agent"}</EntityLink><p className="mt-1 text-xs text-muted-foreground">{agent.office_name || "Independent agent"}</p></div>
              <StatusPill status={agent.status} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <Link to={operationScopeUrl("properties", "agent_id", agent.user_id)} title="View agent properties"><MiniMetric label="Properties" value={agent.property_count} /></Link>
              <Link to={operationScopeUrl("leads", "agent_id", agent.user_id)} title="View agent leads"><MiniMetric label="Open leads" value={agent.open_lead_count} /></Link>
              <Link to={operationScopeUrl("site-visits", "agent_id", agent.user_id)} title="View agent site visits"><MiniMetric label="Visits" value={agent.upcoming_site_visit_count} /></Link>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Last activity {formatDateTime(agent.last_activity_at, true)}</p>
          </article>
        ))}
      </div>
    </>
  );
}

function PropertyTable({ rows, compact = false }) {
  if (!rows.length) return <EmptyResults compact />;
  const shownRows = compact ? rows.slice(0, 6) : rows;
  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[980px] text-sm">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-3">Property</th><th className="px-3 py-3">Listed by</th><th className="px-3 py-3">Office</th>
            <th className="px-3 py-3 text-right">Leads</th><th className="px-3 py-3 text-right">Lead buyers</th><th className="px-3 py-3 text-right">Site visits</th>
            <th className="px-3 py-3">Status</th><th className="px-3 py-3">Last activity</th>
          </tr></thead>
          <tbody>
            {shownRows.map((property) => (
              <tr key={property.property_id} className="border-b border-border/70 last:border-0 hover:bg-secondary/40">
                <td className="px-3 py-4">
                  <EntityLink type="property" id={property.property_id} title="Open property details" className="font-bold text-foreground">{property.title || "Untitled property"}</EntityLink>
                  <p className="mt-0.5 text-xs text-muted-foreground">{property.property_code || "No code"} · {labelize(property.category)}</p>
                  <p className="mt-0.5 text-xs font-semibold text-foreground">{formatPrice(property.price)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Added {formatDateTime(property.created_at, true)}</p>
                </td>
                <td className="px-3 py-4">
                  {property.listed_by_type === "OFFICE" && property.office_id ? <EntityLink type="office" id={property.office_id} title="Open listing office">{property.listed_by_name}</EntityLink>
                    : property.listed_by_user_id ? <EntityLink type="user" id={property.listed_by_user_id} title={`Open listing ${labelize(property.listed_by_type)}`}>{property.listed_by_name}</EntityLink>
                    : <span className="text-muted-foreground">Unknown</span>}
                  <p className="mt-0.5 text-xs text-muted-foreground">{labelize(property.listed_by_type)}</p>
                </td>
                <td className="px-3 py-4">{property.office_id ? <EntityLink type="office" id={property.office_id} title="Open office details">{property.office_name || "Assigned office"}</EntityLink> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums"><Link to={operationScopeUrl("leads", "property_id", property.property_id)} title="View leads for this property" className="text-primary hover:underline">{formatCount(property.open_lead_count)} / {formatCount(property.lead_count)}</Link></td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums"><Link to={operationScopeUrl("leads", "property_id", property.property_id)} title="View buyers who generated leads for this property" className="text-primary hover:underline">{formatCount(property.lead_buyer_count)}</Link></td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums"><Link to={operationScopeUrl("site-visits", "property_id", property.property_id)} title="View site visits for this property" className="text-primary hover:underline">{formatCount(property.upcoming_site_visit_count)} / {formatCount(property.site_visit_count)}</Link></td>
                <td className="px-3 py-4"><StatusPill status={property.status} /></td>
                <td className="px-3 py-4 text-muted-foreground">{formatDateTime(property.last_activity_at, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-3 lg:hidden">
        {shownRows.map((property) => (
          <article key={property.property_id} className="rounded-2xl border border-border bg-background p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><EntityLink type="property" id={property.property_id} title="Open property details" className="line-clamp-2 font-bold text-foreground">{property.title || "Untitled property"}</EntityLink><p className="mt-1 text-xs text-muted-foreground">{property.property_code} · {property.locality || property.city || "Location pending"}</p><p className="mt-1 text-[11px] text-muted-foreground">Added {formatDateTime(property.created_at, true)}</p></div>
              <StatusPill status={property.status} />
            </div>
            <p className="mt-3 text-sm">Listed by <span className="font-semibold">{property.listed_by_name || "Unknown"}</span> · {labelize(property.listed_by_type)}</p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center"><Link to={operationScopeUrl("leads", "property_id", property.property_id)} title="View property leads"><MiniMetric label="Leads" value={property.lead_count} /></Link><Link to={operationScopeUrl("leads", "property_id", property.property_id)} title="View property lead buyers"><MiniMetric label="Buyers" value={property.lead_buyer_count} /></Link><Link to={operationScopeUrl("site-visits", "property_id", property.property_id)} title="View property site visits"><MiniMetric label="Visits" value={property.site_visit_count} /></Link></div>
          </article>
        ))}
      </div>
    </>
  );
}

function LeadTable({ rows }) {
  if (!rows.length) return <EmptyResults />;
  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[1040px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-3">Lead / buyer</th><th className="px-3 py-3">Property</th>
              <th className="px-3 py-3">Assigned agent</th><th className="px-3 py-3">Office</th>
              <th className="px-3 py-3">Source</th><th className="px-3 py-3 text-right">Site visits</th>
              <th className="px-3 py-3">Status</th><th className="px-3 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((lead) => (
              <tr key={lead.lead_id} className="border-b border-border/70 last:border-0 hover:bg-secondary/40">
                <td className="px-3 py-4">
                  <EntityLink type="lead" id={lead.lead_id} title="Open CRM lead" className="font-bold text-foreground">{shortId(lead.lead_id)}</EntityLink>
                  <div className="mt-1">{lead.buyer_user_id ? <EntityLink type="user" id={lead.buyer_user_id} title="Open buyer profile">{lead.buyer_name || "Unknown buyer"}</EntityLink> : <span>{lead.buyer_name || "Unknown buyer"}</span>}</div>
                </td>
                <td className="px-3 py-4">{lead.property_id ? <><EntityLink type="property" id={lead.property_id} title="Open property details" className="font-semibold text-foreground">{lead.property_title || "Property unavailable"}</EntityLink><p className="mt-0.5 text-xs text-muted-foreground">{lead.property_code || "No code"}</p></> : <span className="text-muted-foreground">General enquiry</span>}</td>
                <td className="px-3 py-4">{lead.assigned_agent_id ? <EntityLink type="user" id={lead.assigned_agent_id} title="Open assigned agent">{lead.assigned_agent_name || "Assigned agent"}</EntityLink> : <Link to="/dashboard/admin/lead-assignments" title="Assign this lead" className="font-bold text-warning hover:underline">Unassigned</Link>}</td>
                <td className="px-3 py-4">{lead.assigned_office_id ? <EntityLink type="office" id={lead.assigned_office_id} title="Open assigned office">{lead.assigned_office_name || "Assigned office"}</EntityLink> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-4"><span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{labelize(lead.source)}</span></td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums">{formatCount(lead.site_visit_count)}</td>
                <td className="px-3 py-4"><StatusPill status={lead.status} /></td>
                <td className="px-3 py-4"><p className="text-muted-foreground">{formatDateTime(lead.updated_at, true)}</p>{lead.next_followup_at && <p className="mt-1 text-xs font-semibold text-warning">Follow-up {formatDateTime(lead.next_followup_at, true)}</p>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-3 lg:hidden">
        {rows.map((lead) => (
          <article key={lead.lead_id} className="rounded-2xl border border-border bg-background p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><EntityLink type="lead" id={lead.lead_id} title="Open CRM lead" className="font-bold text-foreground">Lead {shortId(lead.lead_id)}</EntityLink><p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{labelize(lead.source)} · updated {formatDateTime(lead.updated_at, true)}</p></div>
              <StatusPill status={lead.status} />
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <p>Buyer: {lead.buyer_user_id ? <EntityLink type="user" id={lead.buyer_user_id} title="Open buyer profile" className="font-semibold">{lead.buyer_name || "Unknown buyer"}</EntityLink> : <span className="font-semibold">{lead.buyer_name || "Unknown buyer"}</span>}</p>
              <p>Property: {lead.property_id ? <EntityLink type="property" id={lead.property_id} title="Open property details" className="font-semibold">{lead.property_title || "Property unavailable"}</EntityLink> : <span className="font-semibold">General enquiry</span>}</p>
              <p>Agent: {lead.assigned_agent_id ? <EntityLink type="user" id={lead.assigned_agent_id} title="Open assigned agent" className="font-semibold">{lead.assigned_agent_name || "Assigned agent"}</EntityLink> : <span className="font-semibold text-warning">Unassigned</span>}</p>
              <p>Office: <span className="font-semibold">{lead.assigned_office_name || "Not assigned"}</span></p>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-xl bg-secondary px-3 py-2 text-xs"><span className="text-muted-foreground">Related site visits</span><span className="font-black tabular-nums text-foreground">{formatCount(lead.site_visit_count)}</span></div>
          </article>
        ))}
      </div>
    </>
  );
}

function VisitTable({ rows, compact = false }) {
  if (!rows.length) return <EmptyResults compact />;
  const shownRows = compact ? rows.slice(0, 6) : rows;
  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[980px] text-sm">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-3">Schedule</th><th className="px-3 py-3">Property</th><th className="px-3 py-3">Buyer</th>
            <th className="px-3 py-3">Agent</th><th className="px-3 py-3">Office</th><th className="px-3 py-3">Lead</th><th className="px-3 py-3">Status</th>
          </tr></thead>
          <tbody>
            {shownRows.map((visit) => (
              <tr key={visit.site_visit_id} className={`border-b border-border/70 last:border-0 hover:bg-secondary/40 ${visit.overdue ? "bg-destructive/5" : ""}`}>
                <td className="px-3 py-4"><p className="font-semibold text-foreground">{formatDateTime(visit.visit_at)}</p>{visit.overdue && <p className="mt-1 text-xs font-bold text-destructive">Overdue</p>}</td>
                <td className="px-3 py-4"><EntityLink type="property" id={visit.property_id} title="Open property details" className="font-bold text-foreground">{visit.property_title || "Property unavailable"}</EntityLink><p className="mt-0.5 text-xs text-muted-foreground">{visit.property_code || "No code"}</p></td>
                <td className="px-3 py-4">{visit.buyer_user_id ? <EntityLink type="user" id={visit.buyer_user_id} title="Open buyer profile">{visit.buyer_name || "Unknown buyer"}</EntityLink> : <span>{visit.buyer_name || "Unknown buyer"}</span>}</td>
                <td className="px-3 py-4">{visit.agent_id ? <EntityLink type="user" id={visit.agent_id} title="Open assigned agent">{visit.agent_name || "Assigned agent"}</EntityLink> : <span className="font-semibold text-warning">Unassigned</span>}</td>
                <td className="px-3 py-4">{visit.office_id ? <EntityLink type="office" id={visit.office_id} title="Open office details">{visit.office_name || "Assigned office"}</EntityLink> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-4">{visit.lead_id ? <EntityLink type="lead" id={visit.lead_id} title="Open CRM lead">View lead</EntityLink> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-4"><StatusPill status={visit.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-3 lg:hidden">
        {shownRows.map((visit) => (
          <article key={visit.site_visit_id} className={`rounded-2xl border p-4 ${visit.overdue ? "border-destructive/30 bg-destructive/5" : "border-border bg-background"}`}>
            <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{formatDateTime(visit.visit_at)}</p><EntityLink type="property" id={visit.property_id} title="Open property details" className="mt-1 line-clamp-2 font-bold text-foreground">{visit.property_title || "Property unavailable"}</EntityLink></div><StatusPill status={visit.status} /></div>
            <div className="mt-4 space-y-2 text-sm"><p>Buyer: <span className="font-semibold">{visit.buyer_name || "Unknown"}</span></p><p>Agent: <span className={`font-semibold ${visit.agent_id ? "" : "text-warning"}`}>{visit.agent_name || "Unassigned"}</span></p><p>Office: <span className="font-semibold">{visit.office_name || "Not assigned"}</span></p></div>
          </article>
        ))}
      </div>
    </>
  );
}

function AttentionCard({ title, subtitle, items, empty, type }) {
  return (
    <article className="rounded-3xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3"><div><h2 className="font-display text-xl font-black text-foreground">{title}</h2><p className="mt-1 text-xs text-muted-foreground">{subtitle}</p></div><span className={`rounded-xl p-2 ${items.length ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>{items.length ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}</span></div>
      {items.length === 0 ? <p className="mt-6 rounded-2xl bg-success/5 p-4 text-sm text-success">{empty}</p> : (
        <div className="mt-4 divide-y divide-border">
          {items.map((item) => (
            <div key={item.lead_id || item.site_visit_id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <EntityLink type="property" id={item.property_id} title="Open property details" className="line-clamp-1 text-sm font-bold text-foreground">{item.property_title}</EntityLink>
                <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{item.buyer_name}{type === "visit" ? ` · ${formatDateTime(item.visit_at)}` : ` · ${formatDateTime(item.created_at, true)}`}</p>
              </div>
              {type === "lead" ? <EntityLink type="lead" id={item.lead_id} title="Open CRM lead" className="shrink-0">Open</EntityLink> : <StatusPill status={item.status} />}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function PreviewSection({ title, subtitle, action, onAction, children }) {
  return (
    <section className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
      <header className="flex flex-col gap-3 border-b border-border p-5 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 className="font-display text-xl font-black text-foreground">{title}</h2><p className="mt-1 text-xs text-muted-foreground">{subtitle}</p></div>
        <Button variant="outline" size="sm" onClick={onAction} title={action}>{action}<ArrowRight className="ml-2 h-4 w-4" /></Button>
      </header>
      <div className="p-3 sm:p-5">{children}</div>
    </section>
  );
}

function GlobalSearchResults({ items, loading, onClose }) {
  return (
    <div className="absolute right-0 top-full z-30 mt-2 max-h-[28rem] w-full overflow-y-auto rounded-2xl border border-border bg-popover p-2 text-popover-foreground shadow-2xl">
      {loading && items.length === 0 ? <div className="space-y-2 p-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>
        : items.length === 0 ? <p className="p-5 text-center text-sm text-muted-foreground">No matching operation records.</p>
        : items.map((item) => (
          <Link key={`${item.entity_type}-${item.entity_id}`} to={searchResultUrl(item)} onClick={onClose} title={`Open ${labelize(item.entity_type)}`} className="flex items-center justify-between gap-3 rounded-xl p-3 transition-colors duration-200 ease-out hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <div className="min-w-0"><p className="line-clamp-1 text-sm font-bold">{item.title}</p><p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{item.subtitle || labelize(item.entity_type)}</p></div>
            <div className="flex shrink-0 items-center gap-2"><StatusPill status={item.status || item.entity_type} /><ArrowRight className="h-4 w-4 text-muted-foreground" /></div>
          </Link>
        ))}
    </div>
  );
}

function FilterSelect({ value, onChange, options, emptyLabel }) {
  return (
    <div className="relative">
      <Filter className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-muted-foreground" />
      <select value={value} onChange={(event) => onChange(event.target.value)} className="h-11 w-full appearance-none rounded-xl border border-input bg-background pl-10 pr-3 text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-ring" title={emptyLabel}>
        {options.map((option) => <option key={option || "all"} value={option}>{option ? labelize(option) : emptyLabel}</option>)}
      </select>
    </div>
  );
}

function EntityLink({ type, id, title, className = "", children }) {
  if (!id) return <span className={className}>{children}</span>;
  const paths = {
    user: `/dashboard/admin/users?select=${encodeURIComponent(id)}`,
    office: `/dashboard/admin/offices?select=${encodeURIComponent(id)}`,
    property: `/dashboard/admin/properties?select=${encodeURIComponent(id)}`,
    lead: `/dashboard/admin/leads?select=${encodeURIComponent(id)}`,
  };
  const path = paths[type];
  if (!path) return <span className={className}>{children}</span>;
  return <Link to={path} title={title} className={`text-primary hover:underline ${className}`}>{children}</Link>;
}

function StatusPill({ status }) {
  const normalized = String(status || "UNKNOWN").toUpperCase();
  const success = ["ACTIVE", "PUBLISHED", "VERIFIED", "COMPLETED", "WON"].includes(normalized);
  const danger = ["BLOCKED", "REJECTED", "CANCELLED", "NO_SHOW", "OVERDUE", "LOST", "CLOSED_LOST"].includes(normalized);
  const warning = ["PENDING", "PENDING_VERIFICATION", "REQUESTED", "SCHEDULED", "IN_PROCESS", "ASSIGNED", "NEW"].includes(normalized);
  const tone = success ? "border-success/25 bg-success/10 text-success" : danger ? "border-destructive/25 bg-destructive/10 text-destructive" : warning ? "border-warning/25 bg-warning/10 text-warning-foreground" : "border-border bg-secondary text-secondary-foreground";
  return <span className={`inline-flex max-w-[10rem] rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider ${tone}`}>{labelize(normalized)}</span>;
}

function MiniMetric({ label, value }) {
  return <div className="rounded-xl bg-secondary p-2"><p className="font-black tabular-nums text-foreground">{formatCount(value)}</p><p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p></div>;
}

function RowsSkeleton() {
  return <div className="space-y-3">{Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-20 w-full rounded-2xl" />)}</div>;
}

function OverviewSkeleton() {
  return <div className="space-y-6"><div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} className="h-36 rounded-2xl" />)}</div><div className="grid gap-4 xl:grid-cols-2"><Skeleton className="h-72 rounded-3xl" /><Skeleton className="h-72 rounded-3xl" /></div><Skeleton className="h-80 rounded-3xl" /></div>;
}

function EmptyResults({ compact = false }) {
  return <div className={`${compact ? "py-8" : "py-16"} text-center`}><Search className="mx-auto h-8 w-8 text-muted-foreground/50" /><p className="mt-3 font-bold text-foreground">No matching records</p><p className="mt-1 text-sm text-muted-foreground">Try another search or filter.</p></div>;
}

function searchResultUrl(item) {
  const id = encodeURIComponent(item.entity_id);
  if (["AGENT", "BUYER", "OWNER", "USER"].includes(item.entity_type)) return `/dashboard/admin/users?select=${id}`;
  if (item.entity_type === "OFFICE") return `/dashboard/admin/offices?select=${id}`;
  if (item.entity_type === "PROPERTY") return `/dashboard/admin/properties?select=${id}`;
  if (item.entity_type === "LEAD") return `/dashboard/admin/leads?select=${id}`;
  if (item.entity_type === "PLOT_DEMAND") return `/dashboard/admin/plots-on-demand/workbench?select=${id}`;
  if (item.entity_type === "SITE_VISIT") return `/dashboard/admin/site-visits/workbench?select=${id}`;
  return "/dashboard/admin/operations";
}

function operationScopeUrl(view, key, value) {
  const params = new URLSearchParams({ view, [key]: value });
  return `/dashboard/admin/operations?${params.toString()}`;
}

function shortId(value) {
  const text = String(value || "");
  return text.length > 10 ? `${text.slice(0, 8)}…` : (text || "Unknown");
}

function labelize(value) {
  if (!value) return "Unknown";
  if (String(value).toUpperCase() === "PLOT_DEMAND") return "Plots on Demand";
  return String(value).toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatCount(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString("en-IN") : "0";
}

function formatDateTime(value, dateOnly = false) {
  if (!value) return "No activity yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-IN", dateOnly
    ? { day: "2-digit", month: "short", year: "numeric" }
    : { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(parsed);
}
